import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MapPin, Navigation, Power, AlertCircle, CheckCircle } from 'lucide-react';
import { updateDriverLocation, initializeTracking } from '../utils/api';

const DriverTracking = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const bookingId = searchParams.get('booking');
  
  const [isTracking, setIsTracking] = useState(false);
  const [location, setLocation] = useState(null);
  const [watchId, setWatchId] = useState(null);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [updateCount, setUpdateCount] = useState(0);

  useEffect(() => {
    if (!bookingId) {
      setError('No booking ID provided');
    }
  }, [bookingId]);

  const startTracking = async () => {
    if (!bookingId) {
      setError('No booking ID');
      return;
    }

    if (!navigator.geolocation) {
      setError('Geolocation not supported by browser');
      return;
    }

    try {
      // Initialize tracking coordinates
      await initializeTracking(bookingId);
      
      // Start watching position
      const id = navigator.geolocation.watchPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          setLocation({ latitude, longitude });
          
          try {
            await updateDriverLocation(bookingId, latitude, longitude);
            setLastUpdate(new Date());
            setUpdateCount(prev => prev + 1);
            setError(null);
          } catch (err) {
            console.error('Failed to update location:', err);
            setError('Failed to send location update');
          }
        },
        (err) => {
          console.error('Geolocation error:', err);
          setError(`Location error: ${err.message}`);
        },
        {
          enableHighAccuracy: true,
          maximumAge: 5000,
          timeout: 10000
        }
      );
      
      setWatchId(id);
      setIsTracking(true);
      setError(null);
    } catch (err) {
      console.error('Failed to start tracking:', err);
      setError('Failed to initialize tracking');
    }
  };

  const stopTracking = () => {
    if (watchId !== null) {
      navigator.geolocation.clearWatch(watchId);
      setWatchId(null);
    }
    setIsTracking(false);
  };

  useEffect(() => {
    return () => {
      if (watchId !== null) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, [watchId]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 pt-32 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gray-800 rounded-3xl shadow-2xl border border-gray-700 p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-yellow-500/10 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-yellow-500/30">
              <Navigation className="w-8 h-8 text-yellow-500" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Driver Live Tracking</h1>
            <p className="text-gray-400">
              {bookingId ? `Booking: ${bookingId}` : 'No booking selected'}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-red-900/20 border border-red-500/30 rounded-xl p-4 flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-400 font-semibold">Error</p>
                <p className="text-red-300 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Status Card */}
          <div className="bg-gray-900/50 rounded-2xl p-6 mb-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${isTracking ? 'bg-green-500 animate-pulse' : 'bg-gray-500'}`}></div>
                <span className="text-white font-semibold">
                  {isTracking ? 'Tracking Active' : 'Tracking Inactive'}
                </span>
              </div>
              <span className="text-gray-400 text-sm">{updateCount} updates sent</span>
            </div>

            {location && (
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-gray-400 text-sm">Latitude</p>
                  <p className="text-white font-mono">{location.latitude.toFixed(6)}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Longitude</p>
                  <p className="text-white font-mono">{location.longitude.toFixed(6)}</p>
                </div>
              </div>
            )}

            {lastUpdate && (
              <div className="flex items-center space-x-2 text-sm">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span className="text-gray-400">
                  Last update: {lastUpdate.toLocaleTimeString()}
                </span>
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="space-y-4">
            {!isTracking ? (
              <button
                onClick={startTracking}
                disabled={!bookingId}
                className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-4 rounded-xl font-bold text-lg hover:from-green-600 hover:to-green-700 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg"
              >
                <Power className="w-6 h-6" />
                <span>Start Live Tracking</span>
              </button>
            ) : (
              <button
                onClick={stopTracking}
                className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white px-6 py-4 rounded-xl font-bold text-lg hover:from-red-600 hover:to-red-700 transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg"
              >
                <Power className="w-6 h-6" />
                <span>Stop Tracking</span>
              </button>
            )}

            <button
              onClick={() => navigate('/driver-dashboard')}
              className="w-full bg-gray-700 text-white px-6 py-3 rounded-xl font-semibold hover:bg-gray-600 transition-all duration-300"
            >
              Back to Dashboard
            </button>
          </div>

          {/* Instructions */}
          <div className="mt-8 bg-cyan-900/20 border border-cyan-700/30 rounded-xl p-6">
            <h3 className="text-cyan-400 font-bold mb-3 flex items-center">
              <MapPin className="w-5 h-5 mr-2" />
              How it works
            </h3>
            <ul className="text-gray-300 text-sm space-y-2">
              <li>• Click "Start Live Tracking" to begin sending your location</li>
              <li>• Your device will ask for location permission - please allow it</li>
              <li>• Your location updates automatically every 10-15 seconds</li>
              <li>• Customers can see your live location on their tracking page</li>
              <li>• Keep this page open during the trip</li>
              <li>• Click "Stop Tracking" when trip is complete</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverTracking;
